import { State, process } from '@progress/kendo-data-query';
import { formatNumber } from '@progress/kendo-intl';
import {
  GridDataStateChangeEvent,
  GridDetailRowProps,
  GridPagerSettings,
} from '@progress/kendo-react-grid';
import { GridSelectableSettings } from '@progress/kendo-react-grid/dist/npm/interfaces/GridSelectableSettings';
import { isEmpty, isNull, orderBy } from 'lodash';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  useActualizedAmountColumns,
  useActualizedAmountDetailColumns,
} from 'src/assets/static/view_checkbook/actualizedAmountColumns';
import GridWrapper from 'src/components/GridWrapper';
import useViewCheckbook from 'src/hooks/useViewCheckbook';
import useGetActualizedTransactionApi, {
  ActualizedAmountTransaction,
} from 'src/services/useGetActualizedTransactionApi';
import { GLCodesBudgetResponseObject } from 'src/services/useGlCodesBudgetApi';
import exportToExcel from 'src/utils/exportToExcel';
import { isEqualString } from 'src/utils/strings';
import CheckbookBalanceToolBar from './CheckbookBalanceToolBar';

const DATA_ITEM_KEY: string = 'rowId';
const initialDataState: State = {
  skip: 0,
  take: 20,
  sort: [
    {
      field: 'date',
      dir: 'desc',
    },
  ],
};
const gridPagerSettings: GridPagerSettings = {
  buttonCount: 5,
  pageSizes: [10, 20, 50, 100],
};
const selectable: GridSelectableSettings = {
  enabled: true,
  drag: false,
  cell: false,
  mode: 'single',
};
const defaultProps = {
  tableTextId: 'Transactions',
  DataItemKey: DATA_ITEM_KEY,
  sortable: true,
  resizable: true,
  showColumnMenu: true,
  selectable: selectable,
  pageable: gridPagerSettings,
} as const;

interface Props {
  glBudget: GLCodesBudgetResponseObject;
}
interface ActualizedAmountExcelData extends Omit<ActualizedAmountTransaction, 'total' | 'items'> {
  glCode: string;
  fiscalYear: string | undefined;
  period: string | undefined;
  total: string;
  lineItem?: string;
  itemNum?: string;
  description?: string;
  amount?: string;
  companyName?: string;
}

export const fixDateSort = (state: State) => {
  let newSate = state;
  if (state.sort) {
    newSate = {
      ...state,
      sort: state.sort.map((item) =>
        item.field === 'date' ? { ...item, field: 'actualDate' } : item
      ),
    } as State;
  }
  return newSate;
};

const CheckbookActualizedAmountTable: FC<Props> = ({ glBudget }) => {
  const { dropDownPeriodValue, actualizedAmountData, setActualizedAmountData, t, selectedCompany } =
    useViewCheckbook();

  const actualizedAmountColumns = useActualizedAmountColumns();
  const actualizedAmountDetailColumns = useActualizedAmountDetailColumns();

  const { getActualizedTransaction, isSpentLoading } = useGetActualizedTransactionApi();
  const [dataState, setDataState] = useState(initialDataState);
  const [exportLoading, setExportLoading] = useState(false);
  const { data, total } = useMemo(
    () => process(actualizedAmountData || [], fixDateSort(dataState)),
    [dataState, actualizedAmountData]
  );

  const { companyName } = selectedCompany;

  const onDataStateChange = useCallback((e: GridDataStateChangeEvent) => {
    setDataState(e.dataState);
  }, []);

  const handleClearFilter = useCallback(() => {
    setDataState((prev) => ({
      ...initialDataState,
      skip: prev.skip,
      take: prev.take,
    }));
  }, []);

  useEffect(() => {
    if (glBudget.id && dropDownPeriodValue?.glPeriodId) {
      getActualizedTransaction({
        glCodeId: glBudget.id,
        glPeriodId: dropDownPeriodValue?.glPeriodId,
      }).then((res) => {
        setActualizedAmountData(
          orderBy(
            res?.filter((item) => item?.total !== 0),
            'actualDate',
            'desc'
          )
        );
      });
    }
  }, [
    dropDownPeriodValue?.glPeriodId,
    getActualizedTransaction,
    glBudget.id,
    setActualizedAmountData,
  ]);

  const calculateExcelData = useCallback(
    (page: number) => {
      let excelData: ActualizedAmountExcelData[] = [];
      const rows = page === 1 ? (data as ActualizedAmountTransaction[]) : actualizedAmountData;

      rows.forEach((obj) => {
        if (isNull(obj.items) || isEmpty(obj.items)) {
          const newData = {
            ...obj,
            glCode: glBudget.code,
            fiscalYear: dropDownPeriodValue?.fiscalYearDescription,
            period: dropDownPeriodValue?.content,
            total: formatNumber(obj.total, 'c2'),
            notes: isEqualString(obj.notes, 'Journal Entry') ? '' : obj.notes,
            companyName,
          };
          excelData.push(newData);
        } else {
          obj.items.forEach((item) => {
            const newData = {
              ...obj,
              ...item,
              glCode: glBudget.code,
              fiscalYear: dropDownPeriodValue?.fiscalYearDescription,
              period: dropDownPeriodValue?.content,
              total: formatNumber(obj.total, 'c2'),
              amount: formatNumber(item.amount, 'c2'),
              notes: isEqualString(obj.notes, 'Journal Entry') ? '' : obj.notes,
              companyName,
            };
            excelData.push(newData);
          });
        }
      });

      return excelData;
    },
    [
      companyName,
      data,
      dropDownPeriodValue?.content,
      dropDownPeriodValue?.fiscalYearDescription,
      glBudget.code,
      actualizedAmountData,
    ]
  );

  const excelExport = useCallback(
    (page: number) => {
      const rows = calculateExcelData(page);
      const columns: string[] = [];
      const columnMapping: Record<string, string> = {};
      const excelColumns = [
        { field: 'companyName', title: t('Company Name') },
        { field: 'glCode', title: t('GL Code') },
        { field: 'fiscalYear', title: t('Fiscal Year #') },
        { field: 'period', title: t('Accounting Period #') },
        ...actualizedAmountColumns.filter((item) => item?.field !== 'expanded'),
        ...actualizedAmountDetailColumns,
      ];
      excelColumns.forEach((item) => {
        if (item.field === 'date') {
          columns.push('transactionDate');
          Object.assign(columnMapping, { transactionDate: item.title });
        } else {
          columns.push(item.field as string);
          Object.assign(columnMapping, { [item.field as string]: item.title });
        }
      });
      exportToExcel({
        columns,
        rows,
        fileName: t('Actualized Amount Transactions'),
        columnMapping,
        sheetName: t('Actualized Amount Line Items'),
      });
    },
    [actualizedAmountColumns, actualizedAmountDetailColumns, calculateExcelData, t]
  );

  const gridToolbarChildren = useMemo(
    () => (
      <CheckbookBalanceToolBar
        setExportLoading={setExportLoading}
        clearFilter={handleClearFilter}
        excelExport={excelExport}
        dataState={dataState}
      />
    ),
    [dataState, excelExport, handleClearFilter]
  );

  const DetailComponent = useCallback(
    (props: GridDetailRowProps) => (
      <GridWrapper
        dataSource={props.dataItem.items}
        columns={actualizedAmountDetailColumns}
        DataItemKey="field"
        dataState={dataState}
        className="no_Veritcal_ScrollBar"
      />
    ),
    [actualizedAmountDetailColumns, dataState]
  );
  return (
    <GridWrapper
      id="actualizedTransactionGrid"
      columns={actualizedAmountColumns}
      dataSource={data}
      dataState={dataState}
      height={'60vh'}
      total={total}
      onDataStateChange={onDataStateChange}
      gridToolbarChildren={gridToolbarChildren}
      detail={DetailComponent}
      loading={isSpentLoading || exportLoading}
      {...defaultProps}
      isCustomExpandableRow
    />
  );
};

export default CheckbookActualizedAmountTable;
